import { RelationProduct, useGetProductRelations } from "@/pages/products/useProducts";

import ActionsPopover from "@/components/ActionsPopOver/ActionsPopOver";
import ActionsPopOverItem from "@/components/ActionsPopOver/ActionsPopOverItem";
import Skeleton from "@/components/Skeleton/Skeleton";
import PrimarySourceTag from "@/components/StatusTags/PrimarySourceTag/PrimarySourceTag";
import ProxyTag from "@/components/StatusTags/ProxyTag/ProxyTag";
import UnrelatedTag from "@/components/StatusTags/UnrelatedTag";
import Table from "@/components/Table/Table";
import DeleteProxyAction from "@/pages/products/actions/DeleteProxyAction/DeleteProxyAction";
import ManageProxyAction from "@/pages/products/actions/ManageProxyAction/ManageProxyAction";
import { productLabel } from "@/utilities/products";
import { DeleteOutlined, MoreOutlined, SubnodeOutlined } from "@ant-design/icons";
import { Button, Tooltip } from "antd";
import styles from "./../ProductRelations.module.scss";

export default function ProductProxyRelations({ productId }: { productId: number }) {
  const { data, isLoading, isFetching } = useGetProductRelations({
    productId,
  });

  const loading = isLoading || isFetching;

  const proxies =
    data?.filter((product) => product.relation.includes("proxy")).map((product) => product.id) ||
    [];

  const sources =
    data
      ?.filter((product) => product.relation.includes("primary_source"))
      .map((product) => product.id) || [];

  const isSource = proxies.length > 0;
  const isProxy = sources.length > 0;

  return (
    <>
      <div className={[styles.relation, styles.products].join(" ")}>
        <div>
          <h3>Products</h3>
          <span className={styles.metric}>
            <Skeleton
              loading={loading}
              style={{ width: 20, height: 24 }}
            >
              {data?.length}
            </Skeleton>
          </span>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {!loading && !isSource && sources.length === 0 && (
            <ManageProxyAction
              state={"add"}
              proxyIds={[productId]}
              proxyIdsDisabled
            >
              <Button>
                <SubnodeOutlined /> Add proxy
              </Button>
            </ManageProxyAction>
          )}
          {!loading && !isProxy && (
            <ManageProxyAction
              state={"add"}
              sourceId={productId}
              sourceIdDisabled
              multiple={true}
            >
              <Button>
                <SubnodeOutlined /> Use as source
              </Button>
            </ManageProxyAction>
          )}
          {!loading && isSource && proxies.length > 0 && (
            <DeleteProxyAction proxyIds={proxies}>
              <Button danger>Delete all proxies</Button>
            </DeleteProxyAction>
          )}
        </div>
      </div>
      {data && data.length > 0 && (
        <Table<RelationProduct>
          loading={loading}
          emptyState={{ hide: true }}
          hideRaster={true}
          pagination={{ page: 1, pageSize: 5 }}
          data={data}
          rowLink={(record) => `/products/${record.id}`}
          columns={[
            {
              label: "Product",
              key: "id",
              renderer: (record) => productLabel(record.external_id, record.name),
            },
            {
              label: "Multiplier",
              key: "proxy_weight",
              renderer: (record) => `${record.proxy_weight}x`,
            },
            {
              label: "Relation",
              key: "relation",
              renderer: (record) => (
                <>
                  {record.relation.includes("primary_source") ? <PrimarySourceTag /> : <ProxyTag />}
                  {record.relation.includes("unrelated") && (
                    <Tooltip title="Not related to purchase or sales orders">
                      <>
                        <UnrelatedTag />
                      </>
                    </Tooltip>
                  )}
                </>
              ),
            },
            {
              key: "actions",
              label: "Actions",
              minWidth: "80px",
              maxWidth: "80px",
              noLink: true,
              renderer: (product) => (
                <ActionsPopover
                  actions={
                    <>
                      <ManageProxyAction
                        state={"edit"}
                        proxyIds={[
                          product.relation.includes("primary_source") ? productId : product.id,
                        ]}
                        sourceId={
                          product.relation.includes("primary_source") ? product.id : productId
                        }
                        multiplier={product.proxy_weight}
                        proxyIdsDisabled={true}
                        deletable={true}
                      >
                        <ActionsPopOverItem icon={<SubnodeOutlined />}>
                          Manage proxy
                        </ActionsPopOverItem>
                      </ManageProxyAction>
                      <DeleteProxyAction
                        proxyIds={[
                          product.relation.includes("primary_source") ? productId : product.id,
                        ]}
                      >
                        <ActionsPopOverItem
                          danger
                          icon={<DeleteOutlined />}
                        >
                          Delete proxy
                        </ActionsPopOverItem>
                      </DeleteProxyAction>
                    </>
                  }
                >
                  <MoreOutlined />
                </ActionsPopover>
              ),
              align: "end",
            },
          ]}
          rowKey={"id"}
        />
      )}
    </>
  );
}
